/**
 * Les foyers du graphe (19/10/2026) — règles pures, sans React ni store.
 *
 * Le graphe ne dessine plus toute une portée : il dessine ce qui gravite autour de ce que la
 * personne a choisi — des concepts, des vocabulaires, des notes, des dossiers, des documents.
 * Un foyer voyage vers le serveur sous la forme `kind:id` (`GET /api/graph?foyers=…`) ; à
 * l'écran il porte son libellé et sa sorte en mots du lecteur (`grapheLabels.ts`).
 */
import type { Foyer, FoyerKind, FoyerPersonne, GraphNode, NoteSourceRef, PersonneFaceAFaceDto } from '../../types/api';
import { normalizeSearch, UNTITLED_NOTE } from './graphModel';
import { canOpenSourcePassage } from '../sourcePassage';

/** L'ordre des sortes à l'écran : ce qu'on cherche le plus souvent d'abord. */
export const FOYER_KINDS: ReadonlyArray<FoyerKind> = Object.freeze(['entree', 'vocabulaire', 'note', 'perimetre', 'document'] as const);

/** La forme qui voyage (`note:abc`). */
export const foyerKey = (f: Pick<Foyer, 'kind' | 'id'>): string => `${f.kind}:${f.id}`;

/** Les clés d'une liste de foyers, sans doublon, dans l'ordre de choix. */
export const foyerKeys = (foyers: ReadonlyArray<Foyer>): string[] => Array.from(new Set(foyers.map(foyerKey)));

/** Ajoute un foyer s'il n'y est pas déjà (même sorte, même identifiant). */
export function ajouterFoyer(foyers: ReadonlyArray<Foyer>, foyer: Foyer): Foyer[] {
    const key = foyerKey(foyer);
    return foyers.some(f => foyerKey(f) === key) ? [...foyers] : [...foyers, foyer];
}

/** Retire un foyer par sa clé. */
export const retirerFoyer = (foyers: ReadonlyArray<Foyer>, key: string): Foyer[] => foyers.filter(f => foyerKey(f) !== key);

/** Un concept d'autrui que la découverte a trouvé pour l'un de mes concepts choisis (C23 SP6 bis). */
export interface SourceDePersonne { id: string; libelle: string; personne: FoyerPersonne }

/** Ce que le sélecteur fouille : des listes du store, chacune déjà réduite à un identifiant et un libellé. */
export interface SourcesDeFoyers {
    entrees: ReadonlyArray<{ id: string; libelle: string }>;
    vocabulaires: ReadonlyArray<{ id: string; libelle: string }>;
    notes: ReadonlyArray<{ id: string; libelle: string }>;
    perimetres: ReadonlyArray<{ id: string; libelle: string }>;
    documents: ReadonlyArray<{ id: string; libelle: string }>;
    /** « Personnes » (C23 SP6 bis) : des foyers `entree` d'autrui, avec leur prénom et leur porte. */
    personnes?: ReadonlyArray<SourceDePersonne>;
}

/** La sorte d'un groupe de résultats : une sorte de foyer, ou « Personnes » (des foyers `entree` d'autrui). */
export type SorteDeFoyer = FoyerKind | 'personne';

export interface ResultatsDeFoyers {
    /** La sorte des foyers du groupe (`entree` pour « Personnes »). */
    kind: FoyerKind;
    /** Le groupe à l'écran : `kind`, ou `personne`. */
    sorte: SorteDeFoyer;
    foyers: Foyer[];
    /** Des candidats ont été laissés de côté au-delà du plafond par sorte. */
    tronque: boolean;
}

/** Candidats par sorte ; au-delà, « Précisez ». */
export const RESULTATS_PAR_SORTE = 6;

const SOURCE_DE: Record<FoyerKind, keyof SourcesDeFoyers> = {
    entree: 'entrees', vocabulaire: 'vocabulaires', note: 'notes', perimetre: 'perimetres', document: 'documents',
};

/** L'ordre des groupes à l'écran : « Personnes » juste après « Concepts » (C23 SP6 bis). */
const SORTES: ReadonlyArray<SorteDeFoyer> = Object.freeze(['entree', 'personne', 'vocabulaire', 'note', 'perimetre', 'document'] as const);

/** Un libellé qui commence par le texte passe devant ; puis l'ordre alphabétique. */
const parPrefixe = (q: string, texteDe: (c: { libelle: string }) => string) => (a: { libelle: string }, b: { libelle: string }): number => {
    const pa = normalizeSearch(texteDe(a)).startsWith(q) ? 0 : 1;
    const pb = normalizeSearch(texteDe(b)).startsWith(q) ? 0 : 1;
    return (pa - pb) || a.libelle.localeCompare(b.libelle, 'fr', { sensitivity: 'base' });
};

/**
 * Les candidats dont le libellé contient le texte tapé (sans accents ni casse), par sorte dans
 * l'ordre de `SORTES`, les foyers déjà choisis écartés ; rien sans texte. Une sorte sans
 * candidat n'apparaît pas. « Personnes » (C23 SP6 bis) se cherche par le nom du concept **ou** le
 * prénom, dédoublonné par identifiant ; ses foyers sont de sorte `entree`.
 */
export function trouverFoyers(texte: string, sources: SourcesDeFoyers, dejaChoisis: ReadonlyArray<Foyer> = []): ResultatsDeFoyers[] {
    const q = normalizeSearch(texte);
    if (!q) return [];
    const choisis = new Set(dejaChoisis.map(foyerKey));
    const out: ResultatsDeFoyers[] = [];
    for (const sorte of SORTES) {
        if (sorte === 'personne') {
            const vus = new Set<string>();
            const candidats = (sources.personnes ?? [])
                .filter(c => {
                    if (!c.id || !c.libelle || choisis.has(`entree:${c.id}`) || vus.has(c.id)) return false;
                    if (!normalizeSearch(`${c.libelle} ${c.personne.prenom}`).includes(q)) return false;
                    vus.add(c.id);
                    return true;
                })
                .sort(parPrefixe(q, c => `${c.libelle} ${(c as SourceDePersonne).personne.prenom}`));
            if (candidats.length === 0) continue;
            out.push({
                kind: 'entree',
                sorte,
                foyers: candidats.slice(0, RESULTATS_PAR_SORTE).map(c => ({ kind: 'entree' as const, id: c.id, libelle: c.libelle, personne: c.personne })),
                tronque: candidats.length > RESULTATS_PAR_SORTE,
            });
            continue;
        }
        const kind = sorte;
        const liste: ReadonlyArray<{ id: string; libelle: string }> = sources[SOURCE_DE[kind]] ?? [];
        const candidats = liste
            .filter(c => c.id && c.libelle && normalizeSearch(c.libelle).includes(q) && !choisis.has(`${kind}:${c.id}`))
            .sort(parPrefixe(q, c => c.libelle));
        if (candidats.length === 0) continue;
        out.push({
            kind,
            sorte: kind,
            foyers: candidats.slice(0, RESULTATS_PAR_SORTE).map(c => ({ kind, id: c.id, libelle: c.libelle })),
            tronque: candidats.length > RESULTATS_PAR_SORTE,
        });
    }
    return out;
}

/** Ce que la section lit du slice `faceAFaceAvec` (C23 SP6) pour chacun de mes concepts choisis. */
export interface EntreeDeDecouverte { status: string; data?: { personnes: ReadonlyArray<PersonneFaceAFaceDto> } }

/**
 * Les concepts d'autrui que la découverte (« Face à face avec… », C23 SP6) a trouvés pour chacun de
 * mes concepts choisis : un candidat « Personnes » par concept d'autrui (dédoublonné), avec le prénom
 * et la **porte de SP1** (la note lisible rendue, mon concept en face). Le `userId` de la personne
 * n'entre jamais dans un foyer. Pure : les entrées non prêtes ne donnent rien.
 */
export function personnesDecouvertes(foyers: ReadonlyArray<Foyer>, byEntreeId: Readonly<Record<string, EntreeDeDecouverte | undefined>>): SourceDePersonne[] {
    const out: SourceDePersonne[] = [];
    const vus = new Set<string>();
    for (const f of foyers) {
        if (f.kind !== 'entree' || f.personne) continue;
        const entry = byEntreeId[f.id];
        if (!entry || entry.status !== 'ready') continue;
        for (const p of entry.data?.personnes ?? []) {
            if (!p.entreeId || vus.has(p.entreeId)) continue;
            vus.add(p.entreeId);
            out.push({
                id: p.entreeId,
                libelle: p.cle,
                personne: { prenom: typeof p.displayName === 'string' ? p.displayName : '', porte: { noteId: p.noteId, mienEntreeId: f.id, mienLibelle: f.libelle } },
            });
        }
    }
    return out;
}

/** La porte de SP1 portée par un foyer venu de « Personnes » : ce qu'il faut pour ouvrir le face à face. */
export interface PorteFaceAFace { noteId: string; entreeId: string; cle: string; prenom: string; mienEntreeId: string }

const porteDuFoyer = (f: Foyer): PorteFaceAFace | null =>
    (f.kind === 'entree' && f.personne?.porte
        ? { noteId: f.personne.porte.noteId, entreeId: f.id, cle: f.libelle, prenom: f.personne.prenom, mienEntreeId: f.personne.porte.mienEntreeId }
        : null);

/** La porte connue pour le concept `entreeId` (un foyer de « Personnes »), ou `null`. */
export function porteDuFaceAFace(foyers: ReadonlyArray<Foyer>, entreeId: string): PorteFaceAFace | null {
    for (const f of foyers) {
        if (f.kind === 'entree' && f.id === entreeId) {
            const porte = porteDuFoyer(f);
            if (porte) return porte;
        }
    }
    return null;
}

/** Ce que la section retient d'office au choix d'une personne : son concept, puis le mien (cases 0 et 1). */
export function aRetenirPourPersonne(foyer: Foyer): string[] {
    const porte = porteDuFoyer(foyer);
    return porte ? [`entree:${porte.entreeId}`, `entree:${porte.mienEntreeId}`] : [];
}

/** Les faces à face que la fiche de groupe peut ouvrir : un par foyer de « Personnes » dont le concept est retenu. */
export function ouverturesDuFaceAFace(foyers: ReadonlyArray<Foyer>, retenus: ReadonlyArray<string>): PorteFaceAFace[] {
    const presents = new Set(retenus);
    return foyers.flatMap(f => {
        const porte = porteDuFoyer(f);
        return porte && presents.has(`entree:${porte.entreeId}`) ? [porte] : [];
    });
}

/**
 * Sur la fiche de l'un de **mes** concepts : les personnes que la découverte a trouvées pour lui, en
 * foyers prêts à ajouter, celles déjà choisies écartées ; rien pour un concept d'autrui ou sans identifiant.
 */
export function personnesPourLeNoeud(node: GraphNode, personnes: ReadonlyArray<SourceDePersonne>, dejaChoisis: ReadonlyArray<Foyer>): Foyer[] {
    if (node.kind !== 'entree' || !node.entreeId || node.isOwn === false) return [];
    const choisis = new Set(dejaChoisis.map(foyerKey));
    const vus = new Set<string>();
    return personnes
        .filter(p => {
            if (p.personne.porte?.mienEntreeId !== node.entreeId || choisis.has(`entree:${p.id}`) || vus.has(p.id)) return false;
            vus.add(p.id);
            return true;
        })
        .map(p => ({ kind: 'entree' as const, id: p.id, libelle: p.libelle, personne: p.personne }));
}

/** Le foyer qu'un nœud du graphe peut devenir (« Voir ce qui gravite autour ») ; `null` pour un concept sans identifiant. */
export function foyerDuNoeud(node: GraphNode): Foyer | null {
    if (node.kind === 'note') return { kind: 'note', id: node.noteId, libelle: node.label || UNTITLED_NOTE };
    if (node.kind === 'document') return { kind: 'document', id: node.fileId, libelle: node.label || 'Document' };
    if (!node.entreeId) return null;
    // Un concept d'autrui (C23 SP6 bis) garde son prénom sur la puce ; sans porte : rien ne l'ouvre de plus.
    if (node.isOwn === false) return { kind: 'entree', id: node.entreeId, libelle: node.label, personne: { prenom: node.proprietaire ?? '' } };
    return { kind: 'entree', id: node.entreeId, libelle: node.label };
}

/** Ce qu'il faut au lecteur pour ouvrir le document d'un nœud (jamais d'URL : le lecteur la retrouve par l'identifiant). */
export function sourceRefDuDocument(node: GraphNode): { fileId: string; fileName: string; fileType: string } | null {
    if (node.kind !== 'document') return null;
    return { fileId: node.fileId, fileName: node.label, fileType: node.fileType ?? 'other' };
}

/**
 * Le lecteur peut-il ouvrir ce document ? Faux pour un nœud d'une autre sorte, ou pour un document sans
 * type lisible (le serveur ne le donne que si le fichier existe encore) — fumée M6 du 22/10/2026 : la fiche
 * le dit, au lieu d'un bouton « Ouvrir le document » qui ne faisait rien.
 */
export function documentOuvrable(node: GraphNode): boolean {
    const ref = sourceRefDuDocument(node);
    return !!ref && canOpenSourcePassage(ref as Pick<NoteSourceRef, 'fileName' | 'fileType'>);
}
